/**
 * Puantajlar Sayfası — BOSS + Yönetici ortak.
 *
 * BOSS: tüm aktif personel, tüm puantajları iptal edebilir.
 * Yönetici: kendi şubesindeki aktif personel, sadece kendi verdiği puantajı 1 saat içinde geri alabilir.
 * Tasarım her iki rolde aynıdır (analiz kartları, ay seçici, ikili buton, geçmiş).
 */

import { useEffect, useMemo, useState, useCallback } from 'react'
import {
  Award, ChevronLeft, ChevronRight, Plus, Minus,
  TrendingUp, TrendingDown, X, Building2, Users, RotateCcw, Clock,
  ChevronDown, ChevronUp,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { tumPersonelleriGetir } from '@/lib/services/personel-service'
import { tumSubeleriGetir } from '@/lib/services/sube-service'
import { tumPozisyonlariGetir } from '@/lib/services/pozisyon-service'
import { ayTumPuantajlar, puantajIptal, type Puantaj } from '@/lib/services/puantaj-service'
import { PuantajVerModal } from '@/components/puantaj/PuantajVerModal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import type { KullaniciPersonel, Sube, Pozisyon } from '@/types'

const GERI_ALMA_SURESI_MS = 60 * 60 * 1000  // 1 saat — yönetici için

function buAyStr(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function ayEtiketi(ay: string): string {
  const [y, m] = ay.split('-').map(Number)
  if (!y || !m) return ay
  const aylar = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık']
  return `${aylar[m - 1]} ${y}`
}

function paraTam(n: number): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

interface PersonelOzet {
  artiSayi: number
  artiToplam: number
  eksiSayi: number
  eksiToplam: number  // pozitif olarak tutuyoruz
  net: number
}

interface SubeOzet {
  artiToplam: number
  eksiToplam: number
  net: number
  personelSayisi: number
}

export function PuantajlarPage() {
  const { kullanici, firebaseUser } = useAuth()
  const [donem, setDonem] = useState<string>(buAyStr())

  const [personeller, setPersoneller] = useState<KullaniciPersonel[]>([])
  const [subeler, setSubeler] = useState<Sube[]>([])
  const [pozisyonlar, setPozisyonlar] = useState<Pozisyon[]>([])
  const [puantajlar, setPuantajlar] = useState<Puantaj[]>([])
  const [yukleniyor, setYukleniyor] = useState(true)
  const [hata, setHata] = useState<string | null>(null)

  const [modalAcik, setModalAcik] = useState(false)
  const [hedefPersonel, setHedefPersonel] = useState<KullaniciPersonel | null>(null)
  const [iptalEdilecek, setIptalEdilecek] = useState<Puantaj | null>(null)
  const [acikDetayId, setAcikDetayId] = useState<string | null>(null)

  const isBoss = kullanici?.rol === 'boss'
  const benimUid = firebaseUser?.uid
  // BOSS = sınırsız (null), Yönetici = kendi şubeleri
  const yetkiSubeIdleri = useMemo<string[] | null>(() => {
    if (isBoss) return null
    if (kullanici?.rol !== 'personel') return []
    return (kullanici as KullaniciPersonel).subeIdleri ?? []
  }, [isBoss, kullanici])

  // Yönetici için her dakika tick — "Geri al (Xdk)" sayacı güncellesin
  const [, setTick] = useState(0)
  useEffect(() => {
    if (isBoss) return
    const t = setInterval(() => setTick(x => x + 1), 30000)
    return () => clearInterval(t)
  }, [isBoss])

  const yukle = useCallback(async () => {
    try {
      setYukleniyor(true)
      const [pers, subList, pozList, pntList] = await Promise.all([
        tumPersonelleriGetir(),
        tumSubeleriGetir(),
        tumPozisyonlariGetir(),
        ayTumPuantajlar(donem),
      ])
      setPersoneller(pers)
      setSubeler(subList)
      setPozisyonlar(pozList)
      setPuantajlar(pntList)
      setHata(null)
    } catch (err) {
      console.error(err)
      setHata('Yüklenemedi: ' + (err as Error).message)
    } finally {
      setYukleniyor(false)
    }
  }, [donem])

  useEffect(() => { yukle() }, [yukle])

  // Yetki dahilindeki aktif personeller
  const yetkiliPersoneller = useMemo(() => {
    return personeller.filter((p) => {
      if (!p.aktif) return false
      if (isBoss) return true
      // Sorumlu: kendi şubesinde olanlar
      return p.subeIdleri.some((sid) => yetkiSubeIdleri?.includes(sid) ?? false)
    })
  }, [personeller, isBoss, yetkiSubeIdleri])

  const subeMap = useMemo(() => {
    const m = new Map<string, Sube>()
    subeler.forEach((s) => m.set(s.id, s))
    return m
  }, [subeler])

  const pozisyonMap = useMemo(() => {
    const m = new Map<string, Pozisyon>()
    pozisyonlar.forEach((p) => m.set(p.id, p))
    return m
  }, [pozisyonlar])

  // Aktif (iptal değil) puantajlar
  const aktifPuantajlar = useMemo(
    () => puantajlar.filter((p) => !p.iptal),
    [puantajlar]
  )

  // Personel başına özet
  const personelOzet = useCallback((personelId: string): PersonelOzet => {
    const liste = aktifPuantajlar.filter((p) => p.personelId === personelId)
    let artiSayi = 0, artiToplam = 0, eksiSayi = 0, eksiToplam = 0
    for (const p of liste) {
      if (p.tutar > 0) { artiSayi++; artiToplam += p.tutar }
      else { eksiSayi++; eksiToplam += -p.tutar }
    }
    return { artiSayi, artiToplam, eksiSayi, eksiToplam, net: artiToplam - eksiToplam }
  }, [aktifPuantajlar])

  // BOSS analiz: toplam
  const toplamAnaliz = useMemo(() => {
    let arti = 0, eksi = 0, artiSayi = 0, eksiSayi = 0
    for (const p of aktifPuantajlar) {
      if (p.tutar > 0) { arti += p.tutar; artiSayi++ }
      else { eksi += -p.tutar; eksiSayi++ }
    }
    return { arti, eksi, net: arti - eksi, artiSayi, eksiSayi }
  }, [aktifPuantajlar])

  // BOSS analiz: şube bazlı
  const subeAnaliz = useMemo(() => {
    const m = new Map<string, SubeOzet>()
    subeler.forEach((s) => {
      m.set(s.id, { artiToplam: 0, eksiToplam: 0, net: 0, personelSayisi: 0 })
    })
    // Personel sayısını şubeye göre topla
    for (const p of yetkiliPersoneller) {
      for (const sid of p.subeIdleri) {
        const o = m.get(sid)
        if (o) o.personelSayisi++
      }
    }
    // Puantajları şubeye göre topla — personelin ilk şubesini sayıyoruz
    // (çoklu şube durumda birinci şube fee atfedildi)
    for (const pnt of aktifPuantajlar) {
      const personel = personeller.find((p) => p.id === pnt.personelId)
      if (!personel) continue
      const ilkSube = personel.subeIdleri[0]
      if (!ilkSube) continue
      const o = m.get(ilkSube)
      if (!o) continue
      if (pnt.tutar > 0) o.artiToplam += pnt.tutar
      else o.eksiToplam += -pnt.tutar
      o.net = o.artiToplam - o.eksiToplam
    }
    return m
  }, [aktifPuantajlar, subeler, yetkiliPersoneller, personeller])

  function iptalEdilebilirMi(p: Puantaj): boolean {
    if (p.iptal) return false
    if (isBoss) return true
    if (!benimUid) return false
    if (p.verenId !== benimUid) return false
    const yas = Date.now() - p.olusturmaTarihi.toMillis()
    return yas < GERI_ALMA_SURESI_MS
  }

  function geriKalanDk(p: Puantaj): number {
    const yas = Date.now() - p.olusturmaTarihi.toMillis()
    return Math.max(0, Math.ceil((GERI_ALMA_SURESI_MS - yas) / 60000))
  }

  function ayDegistir(yon: -1 | 1) {
    const [y, m] = donem.split('-').map(Number)
    if (!y || !m) return
    const d = new Date(y, m - 1 + yon, 1)
    setDonem(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
  }

  function modalAc(personel: KullaniciPersonel) {
    setHedefPersonel(personel)
    setModalAcik(true)
  }

  async function iptalOnay() {
    if (!iptalEdilecek || !firebaseUser) return
    try {
      await puantajIptal(iptalEdilecek.id, firebaseUser.uid)
      setIptalEdilecek(null)
      await yukle()
    } catch (err) {
      alert('İptal edilemedi: ' + (err as Error).message)
    }
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <Award className="w-6 h-6 text-amber-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Puantajlar</h1>
          <p className="text-sm text-gray-600 mt-0.5">
            Ödül (+) ve ceza (−) puantajları
          </p>
        </div>
      </div>

      {/* Ay seçici */}
      <div className="card mb-4">
        <div className="flex items-center gap-2">
          <button onClick={() => ayDegistir(-1)} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 text-center font-semibold text-gray-900">
            {ayEtiketi(donem)}
          </div>
          <button onClick={() => ayDegistir(1)} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <ChevronRight className="w-5 h-5" />
          </button>
          <button
            onClick={() => setDonem(buAyStr())}
            className="px-3 py-1.5 text-xs text-brand-600 hover:underline whitespace-nowrap"
          >
            Bu ay
          </button>
        </div>
      </div>

      {hata && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm mb-4">
          {hata}
        </div>
      )}

      {yukleniyor && personeller.length === 0 ? (
        <div className="card text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      ) : (
        <>
          {/* Toplam analiz kartı (BOSS = tümü, Yönetici = kendi şubeleri filtrelenmiş) */}
          {(
            <div className="card bg-gradient-to-br from-brand-500 to-indigo-600 border-0 text-white mb-3">
              <p className="text-xs uppercase tracking-wide opacity-90 mb-2">
                {ayEtiketi(donem)} — Toplam Puantaj
              </p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <div className="flex items-center gap-1 text-xs opacity-75">
                    <TrendingUp className="w-3 h-3" />
                    Ödül (+)
                  </div>
                  <p className="text-2xl font-bold tabular-nums">
                    {paraTam(toplamAnaliz.arti)}
                  </p>
                  <p className="text-[10px] opacity-75">{toplamAnaliz.artiSayi} işlem</p>
                </div>
                <div>
                  <div className="flex items-center gap-1 text-xs opacity-75">
                    <TrendingDown className="w-3 h-3" />
                    Ceza (−)
                  </div>
                  <p className="text-2xl font-bold tabular-nums">
                    {paraTam(toplamAnaliz.eksi)}
                  </p>
                  <p className="text-[10px] opacity-75">{toplamAnaliz.eksiSayi} işlem</p>
                </div>
                <div>
                  <div className="text-xs opacity-75">Net</div>
                  <p className={`text-2xl font-bold tabular-nums ${toplamAnaliz.net >= 0 ? '' : 'text-red-200'}`}>
                    {toplamAnaliz.net >= 0 ? '+' : ''}{paraTam(toplamAnaliz.net)}
                  </p>
                  <p className="text-[10px] opacity-75">₺</p>
                </div>
              </div>
            </div>
          )}

          {/* Şube bazlı analiz (BOSS = tüm şubeler, Yönetici = sadece kendi şubeleri) */}
          {subeler.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
              {subeler
                .filter((s) => yetkiSubeIdleri === null || yetkiSubeIdleri.includes(s.id))
                .map((s) => {
                const ozet = subeAnaliz.get(s.id)
                if (!ozet) return null
                return (
                  <div key={s.id} className="card">
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="w-4 h-4 text-gray-500" />
                      <h3 className="font-semibold text-gray-900 text-sm">{s.isim}</h3>
                      <span className="ml-auto text-[10px] text-gray-500">
                        {ozet.personelSayisi} personel
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                      <div className="bg-green-50 rounded-lg p-1.5">
                        <div className="text-green-700">+</div>
                        <div className="font-bold text-green-900 tabular-nums">
                          {paraTam(ozet.artiToplam)} ₺
                        </div>
                      </div>
                      <div className="bg-red-50 rounded-lg p-1.5">
                        <div className="text-red-700">−</div>
                        <div className="font-bold text-red-900 tabular-nums">
                          {paraTam(ozet.eksiToplam)} ₺
                        </div>
                      </div>
                      <div className={`${ozet.net >= 0 ? 'bg-blue-50' : 'bg-amber-50'} rounded-lg p-1.5`}>
                        <div className={ozet.net >= 0 ? 'text-blue-700' : 'text-amber-700'}>Net</div>
                        <div className={`font-bold tabular-nums ${ozet.net >= 0 ? 'text-blue-900' : 'text-amber-900'}`}>
                          {ozet.net >= 0 ? '+' : ''}{paraTam(ozet.net)} ₺
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Personel listesi */}
          <div className="mb-4">
            <h2 className="text-sm font-semibold text-gray-700 mb-2 px-1 uppercase tracking-wide">
              Personeller ({yetkiliPersoneller.length})
            </h2>
            {yetkiliPersoneller.length === 0 ? (
              <div className="card text-center py-8">
                <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-700">Yetkili olduğun personel yok</p>
              </div>
            ) : (
              <div className="space-y-2">
                {yetkiliPersoneller.map((p) => {
                  const ozet = personelOzet(p.id)
                  const subeAdlari = p.subeIdleri
                    .map((sid) => subeMap.get(sid)?.isim)
                    .filter(Boolean) as string[]
                  const pozAdlari = p.pozisyonIdleri
                    .map((pid) => pozisyonMap.get(pid)?.isim)
                    .filter(Boolean) as string[]

                  return (
                    <div key={p.id} className="card">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 truncate">{p.isim}</h3>
                          <div className="text-xs text-gray-500 mt-0.5 truncate">
                            {subeAdlari.join(', ')}
                            {pozAdlari.length > 0 && (
                              <> • {pozAdlari.join(', ')}</>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full font-semibold tabular-nums">
                              +{paraTam(ozet.artiToplam)} ₺ ({ozet.artiSayi})
                            </span>
                            <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded-full font-semibold tabular-nums">
                              −{paraTam(ozet.eksiToplam)} ₺ ({ozet.eksiSayi})
                            </span>
                            {ozet.net !== 0 && (
                              <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold tabular-nums ${
                                ozet.net > 0 ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'
                              }`}>
                                Net {ozet.net > 0 ? '+' : ''}{paraTam(ozet.net)} ₺
                              </span>
                            )}
                            {(ozet.artiSayi + ozet.eksiSayi) > 0 && (
                              <button
                                onClick={() => setAcikDetayId(acikDetayId === p.id ? null : p.id)}
                                className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 inline-flex items-center gap-0.5"
                              >
                                {acikDetayId === p.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                Detay
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1.5 flex-shrink-0">
                          <button
                            onClick={() => modalAc(p)}
                            className="w-12 h-12 bg-green-600 hover:bg-green-700 text-white rounded-xl flex items-center justify-center transition-colors active:scale-95"
                            title="Ödül (+) ekle"
                          >
                            <Plus className="w-6 h-6" />
                          </button>
                          <button
                            onClick={() => modalAc(p)}
                            className="w-12 h-12 bg-red-600 hover:bg-red-700 text-white rounded-xl flex items-center justify-center transition-colors active:scale-95"
                            title="Ceza (−) ekle"
                          >
                            <Minus className="w-6 h-6" />
                          </button>
                        </div>
                      </div>

                      {acikDetayId === p.id && (
                        <div className="mt-3 pt-3 border-t border-gray-100 space-y-1.5">
                          {aktifPuantajlar
                            .filter((pt) => pt.personelId === p.id)
                            .sort((a, b) => (b.tarih?.toMillis?.() ?? 0) - (a.tarih?.toMillis?.() ?? 0))
                            .map((pt) => {
                              const art = pt.tutar > 0
                              return (
                                <div key={pt.id} className="flex items-start gap-2 text-xs">
                                  <span className={`px-1.5 py-0.5 rounded-lg font-semibold tabular-nums flex-shrink-0 ${
                                    art ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                  }`}>
                                    {art ? '+' : '−'}{paraTam(Math.abs(pt.tutar))} ₺
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-gray-800">{pt.aciklama}</p>
                                    <p className="text-gray-400 text-[11px]">
                                      {pt.tarih.toDate().toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                      {' · '}
                                      {pt.verenRol === 'boss' ? 'BOSS'
                                        : pt.verenRol === 'sistem' ? 'Sistem'
                                        : 'Yönetici'}
                                    </p>
                                  </div>
                                </div>
                              )
                            })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Ay puantaj geçmişi */}
          <div>
            <h2 className="text-sm font-semibold text-gray-700 mb-2 px-1 uppercase tracking-wide">
              {ayEtiketi(donem)} Geçmişi ({aktifPuantajlar.length})
            </h2>
            {aktifPuantajlar.length === 0 ? (
              <div className="card text-center py-8">
                <p className="text-sm text-gray-500">Bu ay henüz puantaj yok.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {aktifPuantajlar
                  .slice()
                  .sort((a, b) => (b.tarih?.toMillis?.() ?? 0) - (a.tarih?.toMillis?.() ?? 0))
                  .map((p) => {
                    const personel = personeller.find((x) => x.id === p.personelId)
                    const arti = p.tutar > 0
                    return (
                      <div key={p.id} className={`card ${arti ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${arti ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
                            {arti ? <Plus className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-gray-900">
                                {personel?.isim ?? 'Personel'}
                              </p>
                              <p className={`font-bold tabular-nums ${arti ? 'text-green-700' : 'text-red-700'}`}>
                                {arti ? '+' : '−'}{paraTam(Math.abs(p.tutar))} ₺
                              </p>
                            </div>
                            <p className="text-sm text-gray-700 mt-0.5">{p.aciklama}</p>
                            <p className="text-[10px] text-gray-500 mt-1">
                              {p.tarih.toDate().toLocaleString('tr-TR', {
                                day: 'numeric', month: 'short',
                                hour: '2-digit', minute: '2-digit',
                              })}
                              {' • '}
                              {p.verenRol === 'sorumlu' || p.verenRol === 'yonetici' ? 'Yönetici' : 'BOSS'}
                            </p>
                          </div>
                          {iptalEdilebilirMi(p) && (
                            isBoss ? (
                              <button
                                onClick={() => setIptalEdilecek(p)}
                                className="p-1 text-gray-400 hover:text-red-600"
                                title="İptal et"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => setIptalEdilecek(p)}
                                className="text-[11px] text-red-600 hover:underline inline-flex items-center gap-0.5 whitespace-nowrap"
                                title={`${geriKalanDk(p)} dakika kaldı`}
                              >
                                <RotateCcw className="w-3 h-3" />
                                Geri al ({geriKalanDk(p)}dk)
                              </button>
                            )
                          )}
                          {!isBoss && !p.iptal && p.verenId === benimUid && !iptalEdilebilirMi(p) && (
                            <span className="text-[10px] text-gray-400 inline-flex items-center gap-0.5">
                              <Clock className="w-3 h-3" />
                              Süre doldu
                            </span>
                          )}
                        </div>
                      </div>
                    )
                  })}
              </div>
            )}
          </div>
        </>
      )}

      <PuantajVerModal
        acik={modalAcik}
        personel={hedefPersonel}
        onKapat={() => setModalAcik(false)}
        onKaydedildi={() => {
          setModalAcik(false)
          yukle()
        }}
      />

      <ConfirmDialog
        acik={!!iptalEdilecek}
        baslik="Puantajı İptal Et"
        mesaj="Bu puantaj kaydı iptal edilecek. Devam mı?"
        onayMetni="İptal Et"
        tehlikeli
        onOnay={iptalOnay}
        onIptal={() => setIptalEdilecek(null)}
      />
    </div>
  )
}
